import React, { useState, useEffect, useRef } from 'react';
import { message, Input } from 'antd'; // Keep Input for TextArea
import { Users, UserPlus, Plus, Save, Trash2, Info, X, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { Button, Checkbox, Spinner, Header, ConfirmationModal } from '@crc-evans/ui-components';
import axiosConfig from '../../services/axiosConfig';
import { useDispatch, useSelector } from 'react-redux';
import { updateDprSkipState } from '../../redux/dpr/action';
const { TextArea } = Input;

// Local Component: CommentBox
const CommentBox = ({ header, value, onChange, placeholder }) => (
  <div style={{ marginTop: '40px' }}>
    <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 500, color: '#64748B', textAlign: 'left' }}>
      {header}
    </h4>
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={6}
      style={{
        width: '100%',
        padding: '16px',
        fontSize: '15px',
        border: '1px solid #E2E8F0',
        borderRadius: '12px',
        resize: 'vertical',
        outline: 'none',
        fontFamily: 'inherit',
        color: '#1F2937',
        backgroundColor: '#FFFFFF'
      }}
    />
  </div>
);

// Single-select for Name – same styling as Downtime Option / Quality Root dropdown
const CustomNameSelect = ({ value, options = [], onChange, placeholder = 'Select person...' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const opts = Array.isArray(options) ? options : [];
  const displayLabel = value || '';

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', minWidth: 0, textAlign: 'left' }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          minHeight: '38px',
          height: '38px',
          boxSizing: 'border-box',
          padding: '4px 12px',
          fontSize: '13px',
          border: isOpen ? '2px solid #3B82F6' : '1px solid #E5E7EB',
          borderRadius: '6px',
          backgroundColor: '#fff',
          color: displayLabel ? '#374151' : '#9CA3AF',
          outline: 'none',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transition: 'all 0.2s',
          overflow: 'hidden'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayLabel || placeholder}
        </span>
        {isOpen ? <ChevronUp size={16} color="#6B7280" /> : <ChevronDown size={16} color="#6B7280" />}
      </div>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          right: 0,
          zIndex: 9999,
          backgroundColor: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          padding: '8px',
          maxHeight: '200px',
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
        >
          {opts.map((entry, index) => {
            const optValue = typeof entry === 'string' ? entry : (entry.name || entry.value || '');
            const optLabel = typeof entry === 'string' ? entry : (entry.name || entry.label || entry.value || '');
            if (!optValue) return null;
            return (
              <div
                key={entry.id || index}
                onClick={() => { onChange(optValue); setIsOpen(false); }}
                style={{
                  padding: '8px 10px',
                  fontSize: '13px',
                  color: '#374151',
                  backgroundColor: value === optValue ? '#F3F4F6' : 'transparent',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  marginBottom: '2px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = value === optValue ? '#F3F4F6' : 'transparent'}
              >
                {optLabel}
              </div>
            );
          })}
          {opts.length === 0 && (
            <div style={{ padding: '8px 10px', fontSize: '13px', color: '#9CA3AF' }}>No options</div>
          )}
        </div>
      )}
    </div>
  );
};

const CustomRoleSelect = ({ value, options, onChange, onAddRole, placeholder = "Role..." }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newRoleInput, setNewRoleInput] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setIsAdding(false);
      setSearchTerm('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [isOpen, options.length, isAdding]);

  const filteredOptions = (options || [])
    .filter((opt) =>
      (opt.roleName || '').toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) =>
      (a.roleName || '').localeCompare(b.roleName || '', undefined, { sensitivity: 'base' })
    );

  const handleAdd = (e) => {
    e.stopPropagation();
    if (newRoleInput.trim()) {
      onAddRole(newRoleInput.trim());
      setNewRoleInput('');
      setIsAdding(false);
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', textAlign: 'left' }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '10px 12px',
          fontSize: '14px',
          border: '1px solid #E5E7EB',
          borderRadius: '6px',
          backgroundColor: '#FFFFFF',
          color: value ? '#1F2937' : '#9CA3AF',
          outline: 'none',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxSizing: 'border-box'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || placeholder}
        </span>
        {isOpen ? <ChevronUp size={16} color="#6B7280" /> : <ChevronDown size={16} color="#6B7280" />}
      </div>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Search Bar */}
          <div style={{ padding: '8px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Search size={14} color="#9CA3AF" />
            <input
              autoFocus
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="Search roles..."
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontSize: '13px',
                color: '#1F2937'
              }}
            />
          </div>

          {/* List */}
          <div
            ref={listRef}
            style={{
              maxHeight: '200px',
              overflowY: 'auto',
              padding: '4px 0'
            }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((role) => (
                <div
                  key={role.roleId}
                  onClick={() => {
                    onChange(role.roleName);
                    setIsOpen(false);
                  }}
                  style={{
                    padding: '8px 12px',
                    fontSize: '14px',
                    color: '#374151',
                    cursor: 'pointer',
                    backgroundColor: value === role.roleName ? '#F3F4F6' : 'transparent',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = value === role.roleName ? '#F3F4F6' : 'transparent'}
                >
                  {role.roleName}
                </div>
              ))
            ) : (
              <div style={{ padding: '12px', fontSize: '13px', color: '#9CA3AF', textAlign: 'center' }}>
                No roles found
              </div>
            )}
            <div
              onClick={(e) => {
                e.stopPropagation();
                setIsAdding(true);
              }}
              style={{
                padding: '8px 12px',
                fontSize: '14px',
                color: '#3B82F6',
                fontWeight: 600,
                cursor: 'pointer',
                borderTop: '1px solid #F3F4F6'
              }}
            >
              + Add new role
            </div>
          </div>

          {/* Footer: Add Role Input - Show only when isAdding is true */}
          {isAdding && (
            <div style={{
              padding: '8px',
              borderTop: '1px solid #F3F4F6',
              backgroundColor: '#F9FAFB',
              display: 'flex',
              gap: '8px',
              alignItems: 'center'
            }}>
              <input
                autoFocus
                value={newRoleInput}
                onChange={(e) => setNewRoleInput(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyPress={(e) => e.key === 'Enter' && handleAdd(e)}
                placeholder="Enter role name"
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  fontSize: '13px',
                  borderRadius: '4px',
                  border: '1px solid #D1D5DB',
                  outline: 'none',
                  color: '#1F2937',
                  backgroundColor: '#FFFFFF'
                }}
              />
              <div style={{ display: 'flex', gap: '4px' }}>
                <button
                  onClick={handleAdd}
                  disabled={!newRoleInput.trim()}
                  style={{
                    padding: '6px 12px',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#FFFFFF',
                    backgroundColor: newRoleInput.trim() ? '#3B82F6' : '#93C5FD',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: newRoleInput.trim() ? 'pointer' : 'default'
                  }}
                >
                  Add
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsAdding(false);
                    setNewRoleInput('');
                  }}
                  style={{
                    padding: '6px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6B7280',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** Role names that should show welder ID / station (matches "Welder", "Lead Welder", etc.). */
const isWelderRoleName = (role) => String(role || '').trim().toLowerCase().includes('weld');

const sortRolesByName = (roles) =>
  [...(roles || [])].sort((a, b) =>
    (a.roleName || '').localeCompare(b.roleName || '', undefined, { sensitivity: 'base' })
  );

/**
 * Map API personnel entry → table row. Station and welder must be on the row (not only originalEntry)
 * so the listing inputs reflect values after fetch, refresh, or add-from-modal.
 */
const mapPersonnelEntryToRow = (entry, index, shift) => {
  const rawStation =
    entry?.allocatedStation ??
    entry?.station ??
    entry?.stationNumber ??
    entry?.allocated_station ??
    null;
  const allocatedStation =
    rawStation !== undefined && rawStation !== null && String(rawStation).trim() !== ''
      ? String(rawStation).trim()
      : '';

  return {
    id: entry.id || Date.now() + index,
    name: entry.name || '',
    role: entry.role?.roleName || '',
    planned: shift === 'day' ? (entry.plannedDayShift || 0) : (entry.plannedNightShift || 0),
    actual: shift === 'day' ? (entry.actualDayShift || 0) : (entry.actualNightShift || 0),
    userId: entry.userId,
    roleId: entry.role?.roleId,
    welderId: entry.welderId ?? null,
    allocatedStation,
    originalEntry: entry
  };
};

/** First non-empty station from candidates (do not use `|| null` — it drops "0"). */
function pickAllocatedStationString(...candidates) {
  for (const c of candidates) {
    if (c === undefined || c === null) continue;
    const s = String(c).trim();
    if (s !== '') return s;
  }
  return null;
}

/**
 * DPR personnel API: send both allocatedStation and station (some backends only map one).
 * Uses number when the value is a plain integer string.
 */
function stationFieldsForPersonnelApi(row, originalEntry, newPerson) {
  const str = pickAllocatedStationString(
    newPerson?.allocatedStation,
    newPerson?.station,
    row?.allocatedStation,
    originalEntry?.allocatedStation,
    originalEntry?.station,
    originalEntry?.stationNumber,
    originalEntry?.allocated_station
  );
  if (str === null) {
    return { allocatedStation: null, station: null };
  }
  const n = parseInt(str, 10);
  const val = !Number.isNaN(n) && String(n) === str ? n : str;
  return { allocatedStation: val, station: val };
}

const PersonnelLog = ({
  personnelData: initialPersonnelData = {},
  onSave,
  isSubmitting = false,
  shift = 'day',
  dprLogId,
  sowId,
  selectedProject,
  isLastDprSection
}) => {
  const [personnelRows, setPersonnelRows] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Totals are always calculated from current row values (UI) for immediate updates in header chips
  const totalPlannedHours = personnelRows.reduce((sum, row) => sum + (parseFloat(row.planned) || 0), 0);
  const totalActualHours = personnelRows.reduce((sum, row) => sum + (parseFloat(row.actual) || 0), 0);
  const dispatch = useDispatch();
  const skipPersonnel = useSelector(state => {
    if (!sowId) return false;
    return state.dprReducer?.skipStatesBySection?.[sowId]?.skipPersonnel || false;
  });
  const [allPersonnelEntries, setAllPersonnelEntries] = useState([]); // Store all entries from API for dropdown
  const [availableRoles, setAvailableRoles] = useState([]); // Store available roles from API
  const [newPersons, setNewPersons] = useState([]); // Store newly added persons before saving
  const [newPersonForm, setNewPersonForm] = useState({
    fullName: '',
    role: '',
    welderId: '',
    allocatedStation: ''
  });
  const [addingNewRole, setAddingNewRole] = useState(null); // Track which row is adding a new role (row.id)
  const [newRoleName, setNewRoleName] = useState(''); // Store the new role name being entered
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [isSkipConfirmOpen, setIsSkipConfirmOpen] = useState(false);

  const handleSkipPersonnelChange = (checked) => {
    if (!sowId) return;
    if (checked) {
      setIsSkipConfirmOpen(true);
    } else {
      dispatch(updateDprSkipState(sowId, 'skipPersonnel', false));
    }
  };

  const handleConfirmSkipPersonnel = () => {
    if (sowId) dispatch(updateDprSkipState(sowId, 'skipPersonnel', true));
    setIsSkipConfirmOpen(false);
  };

  const handleAddRow = () => {
    const newId = Date.now();
    setPersonnelRows([...personnelRows, {
      id: newId,
      name: '',
      role: '',
      planned: 0,
      actual: 0,
      userId: null,
      roleId: null,
      welderId: null,
      allocatedStation: ''
    }]);
  };

  // Fetch personnel data from API
  const fetchPersonnelData = async () => {
    if (!dprLogId) return; // Don't fetch if dprLogId is not available
    setIsLoading(true);
    try {
      const response = await axiosConfig.get(`/dpr-personnel?dprLogId=${dprLogId}&shift=${shift}`);

      console.log('Personnel API Response:', response);
      console.log('Response data:', response.data);
      console.log('Response data type:', typeof response.data);

      // Handle different response structures
      let personnelEntries = null;
      let remarksData = null;
      let rolesData = null;
      let totalPlanned = null;
      let totalActual = null;

      if (response.data) {
        // Check if personnelEntries is directly in response.data
        if (response.data.personnelEntries) {
          personnelEntries = response.data.personnelEntries;
          remarksData = response.data.remarks;
          rolesData = response.data.availableRoles;
          totalPlanned = response.data.totalPlanned;
          totalActual = response.data.totalActual;
        }
        // Check if data is nested in response.data.data
        else if (response.data.data && response.data.data.personnelEntries) {
          personnelEntries = response.data.data.personnelEntries;
          remarksData = response.data.data.remarks;
          rolesData = response.data.data.availableRoles;
          totalPlanned = response.data.data.totalPlanned;
          totalActual = response.data.data.totalActual;
        }
        // Check if response.data is directly an array
        else if (Array.isArray(response.data)) {
          personnelEntries = response.data;
        }
        // Check if response.data is an object with array property
        else if (response.data.data && Array.isArray(response.data.data)) {
          personnelEntries = response.data.data;
        }

        // Also check for availableRoles at root level
        if (!rolesData && response.data.availableRoles) {
          rolesData = response.data.availableRoles;
        }

        // Also check for totals at root level
        if (totalPlanned === null && response.data.totalPlanned !== undefined) {
          totalPlanned = response.data.totalPlanned;
        }
        if (totalActual === null && response.data.totalActual !== undefined) {
          totalActual = response.data.totalActual;
        }
      }

      console.log('Extracted personnelEntries:', personnelEntries);
      console.log('Extracted availableRoles:', rolesData);
      console.log('Is personnelEntries an array?', Array.isArray(personnelEntries));

      // Check for remarks/notes in various probable locations
      let notes = null;
      if (remarksData !== undefined && remarksData !== null) {
        notes = remarksData;
      } else if (response.data?.notes !== undefined && response.data?.notes !== null) {
        notes = response.data.notes;
      } else if (response.data?.data?.notes !== undefined && response.data?.data?.notes !== null) {
        notes = response.data.data.notes;
      } else if (response.data?.dprPersonnel?.notes !== undefined && response.data?.dprPersonnel?.notes !== null) {
        notes = response.data.dprPersonnel.notes;
      }

      console.log('Extracted notes/remarks:', notes);

      // Store available roles from API
      if (rolesData && Array.isArray(rolesData) && rolesData.length > 0) {
        setAvailableRoles(sortRolesByName(rolesData));
        console.log('Setting available roles:', rolesData);
      } else {
        console.warn('No available roles found in API response');
        setAvailableRoles([]);
      }

      if (personnelEntries && Array.isArray(personnelEntries) && personnelEntries.length > 0) {
        // Store all entries for dropdown options
        setAllPersonnelEntries(personnelEntries);

        const mappedRows = personnelEntries.map((entry, index) => mapPersonnelEntryToRow(entry, index, shift));

        console.log('Mapped rows:', mappedRows);
        setPersonnelRows(mappedRows);

        // Set remarks if available (handle empty string correctly)
        if (notes !== null && notes !== undefined) {
          setRemarks(notes);
        } else {
          // Also check if notes are embedded in the response directly under a different key if strictly needed,
          // but the above extraction should cover most cases.
          // If nothing found, don't clear state blindly unless we are sure. 
          // Better to default to empty string if it's the first load, but here we just leave it unless we found data.
          if (notes === "") setRemarks("");
        }


      } else {
        console.warn('No personnel entries found in API response');
        // Clear data if no entries found
        setAllPersonnelEntries([]);
        setPersonnelRows([]);

        // Even if no rows, we might have remarks
        if (notes !== null && notes !== undefined) {
          setRemarks(notes);
        }
      }
    } catch (error) {
      console.error('Error fetching personnel data:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      // Clear data on error
      setAllPersonnelEntries([]);
      // On error, keep existing data or use initialPersonnelData
      if (initialPersonnelData.personnelRows) {
        setPersonnelRows(initialPersonnelData.personnelRows);
      } else {
        setPersonnelRows([]);
      }
      if (initialPersonnelData.remarks) {
        setRemarks(initialPersonnelData.remarks);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch personnel data from API when shift changes
  useEffect(() => {
    // Clear newPersons when shift changes
    setNewPersons([]);
    fetchPersonnelData();
  }, [shift, dprLogId]); // Re-fetch when shift or dprLogId changes

  // Update local state when prop changes (fallback)
  useEffect(() => {
    if (initialPersonnelData.personnelRows && personnelRows.length === 0) {
      setPersonnelRows(initialPersonnelData.personnelRows);
    }
    if (initialPersonnelData.remarks && !remarks) {
      setRemarks(initialPersonnelData.remarks);
    }
  }, [initialPersonnelData]);

  const savePersonnelData = async (override = {}) => {
    if (!dprLogId) {
      console.error('dprLogId is required to save personnel');
      return false;
    }
    const rows = override.rows || personnelRows;
    const roles = override.roles || availableRoles;
    const newPersonsData = override.newPersons || newPersons;
    const notes = override.remarks !== undefined ? override.remarks : remarks;
    setIsSaving(true);
    try {
      // Map personnelRows to API format (existing entries with id)
      const existingEntries = rows
        .filter(row => !row.isNew)
        .map((row) => {
          // Get original entry data to preserve fields
          const originalEntry = row.originalEntry || {};

          // Determine the hours value based on shift
          const hoursValue = parseFloat(row.hours) || 0;

          // Find the role object from availableRoles - use roleId if available, otherwise match by roleName
          const roleObj = (row.roleId && roles.find(r => r.roleId === row.roleId)) ||
            roles.find(r => r.roleName === row.role) ||
            (originalEntry.role ? { roleId: originalEntry.role.roleId, roleName: originalEntry.role.roleName } : null);

          // Get planned and actual values from row
          const plannedValue = parseFloat(row.planned) || 0;
          const actualValue = parseFloat(row.actual) || 0;

          const stationPayload = stationFieldsForPersonnelApi(row, originalEntry, null);

          // Build the entry object
          const entry = {
            id: row.id || originalEntry.id,
            name: row.name || originalEntry.name || '',
            category: originalEntry.category || row.role || '',
            remarks: originalEntry.remarks || '',
            role: roleObj ? {
              roleId: roleObj.roleId,
              roleName: roleObj.roleName
            } : (originalEntry.role || {}),
            welderId: originalEntry.welderId || row.welderId || null,
            ...stationPayload,
          };

          // Update planned and actual hours based on shift
          if (shift === 'day') {
            entry.plannedDayShift = plannedValue;
            entry.plannedNightShift = originalEntry.plannedNightShift || 0;
            entry.actualDayShift = actualValue;
            entry.actualNightShift = originalEntry.actualNightShift || 0;
          } else {
            entry.plannedDayShift = originalEntry.plannedDayShift || 0;
            entry.plannedNightShift = plannedValue;
            entry.actualDayShift = originalEntry.actualDayShift || 0;
            entry.actualNightShift = actualValue;
          }

          return entry;
        });

      // Map newPersons to API format (new entries without id)
      // Get planned/actual values from personnelRows for new persons
      const newEntries = newPersonsData.map((newPerson) => {
        const row = rows.find(r => r.id === newPerson.id && r.isNew);
        const plannedValue = row ? (parseFloat(row.planned) || 0) : 0;
        const actualValue = row ? (parseFloat(row.actual) || 0) : 0;

        const stationPayload = stationFieldsForPersonnelApi(row || {}, {}, newPerson);

        const entry = {
          name: newPerson.name,
          remarks: newPerson.remarks || '',
          role: {
            roleId: newPerson.role.roleId,
            roleName: newPerson.role.roleName
          },
          welderId: newPerson.welderId != null && String(newPerson.welderId).trim() !== ''
            ? String(newPerson.welderId).trim()
            : null,
          ...stationPayload,
        };

        // Update planned and actual hours based on shift
        if (shift === 'day') {
          entry.plannedDayShift = plannedValue;
          entry.plannedNightShift = 0;
          entry.actualDayShift = actualValue;
          entry.actualNightShift = 0;
        } else {
          entry.plannedDayShift = 0;
          entry.plannedNightShift = plannedValue;
          entry.actualDayShift = 0;
          entry.actualNightShift = actualValue;
        }

        return entry;
      });

      // Combine existing and new entries
      const personnelEntries = [...existingEntries, ...newEntries];

      // Calculate totals
      const totalPlanned = personnelEntries.reduce((sum, entry) =>
        sum + (shift === 'day' ? (entry.plannedDayShift || 0) : (entry.plannedNightShift || 0)), 0
      );

      const totalActual = personnelEntries.reduce((sum, entry) =>
        sum + (shift === 'day' ? (entry.actualDayShift || 0) : (entry.actualNightShift || 0)), 0
      );

      // Build the request body
      const requestBody = {
        shift: shift,
        dprPersonnel: {
          dprLogId: dprLogId,
          reportNo: `DPR-${dprLogId}-PE-v1`, // Generate report number
          personnelEntries: personnelEntries,
          shiftLength: 12, // Default or calculate based on requirements
          totalPlanned: totalPlanned,
          totalActual: totalActual,
          notes: notes || '' // Comments/remarks from the textarea go into notes field
        }
      };

      console.log('Remarks/Comments to be saved as notes:', notes);

      console.log('Saving personnel data:', requestBody);

      // Call PUT API
      const response = await axiosConfig.put('/dpr-personnel', requestBody);

      console.log('Personnel save response:', response);

      // Clear newPersons after successful save
      setNewPersons([]);

      // Fetch updated data from GET API to refresh UI
      console.log('Fetching updated personnel data after save...');
      try {
        if (!dprLogId) {
          console.error('dprLogId is required to refresh personnel data');
          message.error('Failed to refresh data: dprLogId is missing');
        } else {
        console.log(`Calling GET API: /dpr-personnel?dprLogId=${dprLogId}&shift=${shift}`);
        const getResponse = await axiosConfig.get(`/dpr-personnel?dprLogId=${dprLogId}&shift=${shift}`);

        console.log('Refreshed Personnel API Response:', getResponse);

        // Handle different response structures
        let personnelEntries = null;
        let remarksData = null;
        let rolesData = null;
        let totalPlanned = null;
        let totalActual = null;

        if (getResponse.data) {
          // Check if personnelEntries is directly in response.data
          if (getResponse.data.personnelEntries) {
            personnelEntries = getResponse.data.personnelEntries;
            remarksData = getResponse.data.remarks;
            rolesData = getResponse.data.availableRoles;
            totalPlanned = getResponse.data.totalPlanned;
            totalActual = getResponse.data.totalActual;
          }
          // Check if data is nested in response.data.data
          else if (getResponse.data.data && getResponse.data.data.personnelEntries) {
            personnelEntries = getResponse.data.data.personnelEntries;
            remarksData = getResponse.data.data.remarks;
            rolesData = getResponse.data.data.availableRoles;
            totalPlanned = getResponse.data.data.totalPlanned;
            totalActual = getResponse.data.data.totalActual;
          }
          // Check if response.data is directly an array
          else if (Array.isArray(getResponse.data)) {
            personnelEntries = getResponse.data;
          }
          // Check if response.data is an object with array property
          else if (getResponse.data.data && Array.isArray(getResponse.data.data)) {
            personnelEntries = getResponse.data.data;
          }

          // Also check for availableRoles at root level
          if (!rolesData && getResponse.data.availableRoles) {
            rolesData = getResponse.data.availableRoles;
          }

          // Also check for totals at root level
          if (totalPlanned === null && getResponse.data.totalPlanned !== undefined) {
            totalPlanned = getResponse.data.totalPlanned;
          }
          if (totalActual === null && getResponse.data.totalActual !== undefined) {
            totalActual = getResponse.data.totalActual;
          }
        }

        // Update available roles
        // Update available roles
        // Merge roles from API (rolesData) with roles found in personnelEntries
        let finalRoles = [];
        if (rolesData && Array.isArray(rolesData)) {
          finalRoles = [...rolesData];
        }

        // Add any roles from personnel entries that aren't in the available list
        if (personnelEntries && Array.isArray(personnelEntries)) {
          personnelEntries.forEach(entry => {
            if (entry.role && entry.role.roleName) {
              const exists = finalRoles.find(r => r.roleName === entry.role.roleName);
              if (!exists) {
                finalRoles.push({
                  roleId: entry.role.roleId || `temp-${Date.now()}-${Math.random()}`,
                  roleName: entry.role.roleName
                });
              }
            }
          });
        }

        if (finalRoles.length > 0) {
          setAvailableRoles(sortRolesByName(finalRoles));
        }

        // Update total planned and actual hours from API


        // Update personnel rows with fresh data
        if (personnelEntries && Array.isArray(personnelEntries) && personnelEntries.length > 0) {
          // Store all entries for dropdown options
          setAllPersonnelEntries(personnelEntries);

          const mappedRows = personnelEntries.map((entry, index) => mapPersonnelEntryToRow(entry, index, shift));

          setPersonnelRows(mappedRows);

          // Update remarks if available
          if (remarksData) {
            setRemarks(remarksData);
          }


        } else {
          console.warn('No personnel entries found in refreshed data');
        }
        }
      } catch (getError) {
        console.error('Error refreshing personnel data after save:', getError);
        console.error('GET API Error details:', getError.response || getError.message);
        message.warning('Data saved successfully, but failed to refresh. Please reload the page.');
        // Don't fail the save operation if refresh fails
      }

      // Show success message
      message.success('Personnel data saved successfully');

      // Call the onSave callback if provided
      if (onSave) {
        onSave({
          personnelRows,
          remarks,
          success: true,
          response: response.data
        });
      }

      return true;
    } catch (error) {
      console.error('Error saving personnel data:', error);
      console.error('Error response:', error.response);

      // Call onSave with error info
      if (onSave) {
        onSave({
          personnelRows,
          remarks,
          success: false,
          error: error.response?.data || error.message
        });
      }
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => savePersonnelData();


  const handleDeleteRow = (id) => {
    setRowToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setRowToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!rowToDelete) return;
    const id = rowToDelete;

    try {
      const row = personnelRows.find(r => String(r.id) === String(id));

      // Update local state optimistic
      const updatedRows = personnelRows.filter(r => String(r.id) !== String(id));
      const updatedNewPersons = row?.isNew
        ? newPersons.filter(p => String(p.id) !== String(id))
        : newPersons;

      setPersonnelRows(updatedRows);
      if (row?.isNew) {
        setNewPersons(updatedNewPersons);
      }

      setIsDeleteModalOpen(false);
      setRowToDelete(null);

      // Call API if needed
      if (dprLogId) {
        setIsSaving(true);
        try {
          await savePersonnelData({
            rows: updatedRows,
            newPersons: updatedNewPersons
          });
          message.success('Personnel deleted successfully');
        } catch (error) {
          // Revert
          setPersonnelRows(personnelRows);
          message.error('Failed to delete personnel');
        } finally {
          setIsSaving(false);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleRowChange = (id, field, value) => {
    if (field === 'name') {
      // When name is selected, auto-populate role from API data
      const selectedEntry = allPersonnelEntries.find(entry => entry.name === value);
      const selStation =
        selectedEntry?.allocatedStation ??
        selectedEntry?.station ??
        selectedEntry?.stationNumber ??
        selectedEntry?.allocated_station;
      const nextStation =
        selStation !== undefined && selStation !== null && String(selStation).trim() !== ''
          ? String(selStation).trim()
          : '';
      setPersonnelRows(personnelRows.map(row =>
        row.id === id
          ? {
            ...row,
            name: value,
            role: selectedEntry?.role?.roleName || row.role,
            userId: selectedEntry?.userId || row.userId,
            roleId: selectedEntry?.role?.roleId || row.roleId,
            welderId: selectedEntry?.welderId ?? row.welderId,
            allocatedStation: isWelderRoleName(selectedEntry?.role?.roleName || row.role)
              ? (nextStation || row.allocatedStation || '')
              : ''
          }
          : row
      ));
    } else if (field === 'role') {
      // When role is selected, also update roleId
      const roleObj = availableRoles.find(r => r.roleName === value);
      setPersonnelRows(personnelRows.map(row =>
        row.id === id
          ? {
            ...row,
            role: value,
            roleId: roleObj ? roleObj.roleId : row.roleId,
            allocatedStation: isWelderRoleName(value) ? (row.allocatedStation || '') : ''
          }
          : row
      ));
    } else {
      setPersonnelRows(personnelRows.map(row =>
        row.id === id
          ? { ...row, [field]: value }
          : row
      ));
    }
  };

  // Calculate total manpower
  const totalManpower = personnelRows.length;

  // Header chips: always show sum of PLANNED HOURS and ACTUAL HOURS from current rows (immediate UI feedback)
  const displayTotalPlanned = totalPlannedHours;
  const displayTotalActual = totalActualHours;



  // Role options will come from API (availableRoles state)

  const handleOpenModal = () => {
    setIsModalVisible(true);
    setNewPersonForm({ fullName: '', role: '', welderId: '', allocatedStation: '' });
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setNewPersonForm({ fullName: '', role: '', welderId: '', allocatedStation: '' });
    // Reset adding new role state if it was set for modal
    if (addingNewRole === 'modal') {
      setAddingNewRole(null);
      setNewRoleName('');
    }
  };

  const handleAddPerson = () => {
    if (!newPersonForm.fullName || !newPersonForm.role) {
      return; // Required fields validation
    }

    if (isWelderRoleName(newPersonForm.role) && !newPersonForm.welderId?.trim()) {
      message.warning('Welder ID is required for welding roles');
      return;
    }

    // Find the role object from availableRoles
    const roleObj = availableRoles.find(r => r.roleName === newPersonForm.role);

    if (!roleObj) {
      console.error('Role not found:', newPersonForm.role);
      return;
    }

    const newId = Date.now();
    const stationStr =
      newPersonForm.allocatedStation != null && String(newPersonForm.allocatedStation).trim() !== ''
        ? String(newPersonForm.allocatedStation).trim()
        : '';
    const newPersonEntry = {
      id: newId,
      name: newPersonForm.fullName,
      role: roleObj.roleName,
      planned: 0,
      actual: 0,
      roleId: roleObj.roleId,
      welderId: newPersonForm.welderId || null,
      allocatedStation: stationStr,
      originalEntry: {
        name: newPersonForm.fullName,
        role: {
          roleId: roleObj.roleId,
          roleName: roleObj.roleName
        },
        welderId: newPersonForm.welderId || null,
        allocatedStation: stationStr || null,
        remarks: '',
        plannedDayShift: 0,
        actualDayShift: 0,
        plannedNightShift: 0,
        actualNightShift: 0
      },
      isNew: true
    };

    // Add to personnelRows for display
    setPersonnelRows([...personnelRows, newPersonEntry]);

    // Also add to newPersons for API payload
    setNewPersons([...newPersons, {
      id: newId,
      name: newPersonForm.fullName,
      role: {
        roleId: roleObj.roleId,
        roleName: roleObj.roleName
      },
      welderId: newPersonForm.welderId || null,
      allocatedStation: stationStr || null,
      remarks: '',
      plannedDayShift: 0,
      actualDayShift: 0,
      plannedNightShift: 0,
      actualNightShift: 0
    }]);

    // Add to allPersonnelEntries so it appears in the Name dropdown
    setAllPersonnelEntries([...allPersonnelEntries, newPersonEntry]);

    handleCloseModal();
  };

  const handleFormChange = (field, value) => {
    if (field === 'role') {
      setNewPersonForm((prev) => ({
        ...prev,
        role: value,
        ...(isWelderRoleName(value) ? {} : { welderId: '', allocatedStation: '' })
      }));
      return;
    }
    setNewPersonForm((prev) => ({ ...prev, [field]: value }));
  };

  // Handle adding a new role via API
  const handleAddNewRole = async (rowId, name) => {
    const roleName = name || newRoleName;
    if (!roleName || !roleName.trim()) {
      return;
    }

    // Create a temporary role object with a temporary ID
    // This will be sent in the PUT API when saving personnel data
    const newRole = {
      roleId: Date.now(), // Temporary ID - will be handled by backend when saving
      roleName: roleName.trim()
    };

    const updatedRoles = sortRolesByName([...availableRoles, newRole]);
    setAvailableRoles(updatedRoles);

    // Update the row with the new role immediately
    // The Select component will work because we're setting the value directly
    if (rowId === 'modal') {
      // Update modal form
      handleFormChange('role', newRole.roleName);
    } else {
      // Update table row - update both role and roleId
      const updatedRows = personnelRows.map(row =>
        row.id === rowId
          ? {
            ...row,
            role: newRole.roleName,
            roleId: newRole.roleId
          }
          : row
      );
      setPersonnelRows(updatedRows);

      await savePersonnelData({ rows: updatedRows, roles: updatedRoles });
    }

    // Reset state
    setAddingNewRole(null);
    setNewRoleName('');

    if (rowId === 'modal') {
      message.success('Role added.');
    }
  };



  // Determine display values for badge
  const mpCount = personnelRows.length;
  // Calculate total hours from rows. The image shows "2.0 MP" and "24 Hrs"
  // Assuming "Hrs" is total actual or planned hours. Let's use planned for now or just the sum.
  const totalHours = personnelRows.reduce((sum, row) => sum + (parseFloat(row.hours) || 12), 0); // Using default 12 for now if missing

  return (
    <div style={{ position: 'relative' }}>
      {isLoading && (
        <div style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255,255,255,0.7)',
          zIndex: 1000
        }}>
          <Spinner variant="inline" size="medium" />
        </div>
      )}

      {/* Main Container */}
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        padding: '32px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '24px'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <div style={{
              backgroundColor: '#F3E8FF',
              padding: '10px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9333EA'
            }}>
              <Users size={24} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0F172A', textAlign: 'left' }}>
                  Personnel & Manpower
                </h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <span style={{
                    backgroundColor: '#F1F5F9',
                    color: '#64748B',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 600
                  }}>
                    Planned: {displayTotalPlanned.toFixed(1)} Hrs
                  </span>
                  <span style={{
                    backgroundColor: '#F1F5F9',
                    color: '#64748B',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 600
                  }}>
                    Actual: {displayTotalActual.toFixed(1)} Hrs
                  </span>
                </div>
              </div>
              <p style={{ margin: 0, fontSize: '14px', color: '#64748B', textAlign: 'left' }}>
                Log team members, roles, and shift hours.
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Checkbox
              label="Skip Personnel"
              checked={skipPersonnel}
              onChange={handleSkipPersonnelChange}
              borderColor="#00C9A7"
            />
            <Button
              label="Add Person"
              variant="primary"
              size="small"
              icon={Plus}
              onClick={handleOpenModal}
            />
          </div>
        </div>

        {/* Table/List */}
        <div style={{ marginBottom: '32px' }}>
          {/* Header Row */}
          <div style={{ marginBottom: '16px' }}>
            <Header
              items={[
                {
                  label: 'NAME',
                  width: '10%'
                },
                {
                  align: 'center',
                  label: 'ROLE / DESIGNATION',
                  width: 'flex: 1'
                },
                {
                  align: 'center',
                  label: 'STATION',
                  width: '12%'
                },
                {
                  align: 'center',
                  label: 'PLANNED HOURS',
                  width: '12%'
                },
                {
                  align: 'center',
                  label: 'ACTUAL HOURS',
                  width: '12%'
                },
                {
                  align: 'center',
                  label: 'ACTION',
                  width: '10%'
                }
              ]}
              size="small"
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
            {personnelRows.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px', color: '#94A3B8' }}>
                No personnel added.
              </div>
            ) : (
              personnelRows.map((row) => (
                <div
                  key={row.id}
                  onMouseEnter={() => setHoveredRow(row.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '16px',
                    border: hoveredRow === row.id ? '1px solid #00C9A7' : '1px solid #E5E7EB',
                    borderRadius: '8px',
                    backgroundColor: '#FFFFFF',
                    transition: 'border-color 0.2s ease'
                  }}>
                  {/* Name - same dropdown styling as Downtime Option / Quality Root */}
                  <div style={{ width: '30%' }}>
                    <CustomNameSelect
                      value={row.name || ''}
                      options={allPersonnelEntries}
                      onChange={(val) => handleRowChange(row.id, 'name', val)}
                      placeholder="Select person..."
                    />
                  </div>

                  {/* Role - Custom Select Dropdown */}
                  <div style={{ flex: 1 }}>
                    <CustomRoleSelect
                      value={row.role || ''}
                      options={availableRoles}
                      onChange={(val) => handleRowChange(row.id, 'role', val)}
                      onAddRole={(name) => handleAddNewRole(row.id, name)}
                    />
                  </div>

                  {/* Station (Welder only) */}
                  <div style={{ width: '12%' }}>
                    {isWelderRoleName(row.role) ? (
                      <input
                        type="text"
                        placeholder="Station #"
                        value={row.allocatedStation != null ? String(row.allocatedStation) : ''}
                        onChange={(e) => handleRowChange(row.id, 'allocatedStation', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #E5E7EB',
                          background: '#fff',
                          color: '#1F2937',
                          fontSize: '13px',
                          boxSizing: 'border-box',
                        }}
                      />
                    ) : (
                      <div style={{ color: '#9CA3AF', fontSize: '13px', textAlign: 'center' }}>—</div>
                    )}
                  </div>

                  {/* Planned Hours */}
                  <div style={{
                    width: '12%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '6px 8px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px',
                      backgroundColor: '#FFFFFF'
                    }}>
                      <input
                        type="number"
                        value={row.planned || 0}
                        onChange={(e) => handleRowChange(row.id, 'planned', parseFloat(e.target.value) || 0)}
                        style={{
                          width: '40px',
                          padding: '0',
                          fontSize: '14px',
                          fontWeight: 600,
                          border: 'none',
                          backgroundColor: 'transparent',
                          color: '#1F2937',
                          textAlign: 'center',
                          outline: 'none'
                        }}
                      />
                      <span style={{
                        fontSize: '13px',
                        color: '#9CA3AF',
                        fontWeight: 400,
                        marginLeft: '4px'
                      }}>Hrs</span>
                    </div>
                  </div>

                  {/* Actual Hours - Number Display */}
                  <div style={{
                    width: '12%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '6px 8px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px',
                      backgroundColor: '#FFFFFF'
                    }}>
                      <input
                        type="number"
                        value={row.actual || 0}
                        onChange={(e) => handleRowChange(row.id, 'actual', parseFloat(e.target.value) || 0)}
                        style={{
                          width: '40px',
                          padding: '0',
                          fontSize: '14px',
                          fontWeight: 600,
                          border: 'none',
                          backgroundColor: 'transparent',
                          color: '#1F2937',
                          textAlign: 'center',
                          outline: 'none'
                        }}
                      />
                      <span style={{
                        fontSize: '13px',
                        color: '#9CA3AF',
                        fontWeight: 400,
                        marginLeft: '4px'
                      }}>Hrs</span>
                    </div>
                  </div>

                  {/* Action - Delete */}
                  <div style={{ width: '10%', display: 'flex', justifyContent: 'center' }}>
                    <button
                      onClick={() => handleDeleteRow(row.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#D1D5DB',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#EF4444'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#D1D5DB'}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <CommentBox
          header="Personnel Notes / Remarks"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Enter any additional personnel remarks..."
        />

        {/* Save Button - Sticky */}
        <div style={{
          position: 'sticky',
          bottom: '24px',
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '24px',
          paddingTop: '24px',
          zIndex: 10
        }}>
          <Button
            label={
              typeof isLastDprSection === 'boolean'
                ? (isLastDprSection ? 'Save section' : 'Save & Next')
                : 'Save Personnel Report'
            }
            variant="secondary"
            icon={Save}
            onClick={handleSave}
            disabled={isSaving || isSubmitting}
          />
        </div>

      </div >

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        title="Delete Personnel"
        message="Are you sure you want to delete this person? This action cannot be undone."
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isOpen={isDeleteModalOpen}
        size="medium"
        confirmButtonProps={{
          style: {
            backgroundColor: '#FF3B30', // Red color matching reference visual (approx) or use #EF4444
            borderColor: '#FF3B30'
          }
        }}
      />
      <ConfirmationModal
        title="Skip Personnel"
        message={`Are you sure you want to skip the Personnel log for ${shift === 'night' ? 'night' : 'day'} shift?`}
        confirmText="Confirm"
        cancelText="Cancel"
        isOpen={isSkipConfirmOpen}
        onConfirm={handleConfirmSkipPersonnel}
        onCancel={() => setIsSkipConfirmOpen(false)}
        size="medium"
      />



      {/* Add New Person Modal - Keeping existing modal logic but ensuring it matches basic style or is hidden properly */}
      {
        isModalVisible && (
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingTop: '100px',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              width: '500px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'visible'
            }}>
              {/* Header */}
              <div style={{
                padding: '24px',
                borderBottom: '1px solid #E5E7EB',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start'
              }}>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{
                    backgroundColor: '#EEF2FF',
                    padding: '10px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#4F46E5'
                  }}>
                    <UserPlus size={24} />
                  </div>
                  <div>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: 600, color: '#111827', textAlign: 'left' }}>Add New Person</h3>
                    <p style={{ margin: 0, fontSize: '14px', color: '#6B7280', textAlign: 'left' }}>Enter details for the new team member.</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#9CA3AF',
                    padding: '4px'
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Body */}
              <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151',
                    textAlign: 'left'
                  }}>
                    Full Name <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. John Doe"
                    value={newPersonForm.fullName}
                    onChange={(e) => handleFormChange('fullName', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: '1px solid #D1D5DB',
                      fontSize: '14px',
                      outline: 'none',
                      color: '#1F2937',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                    onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151',
                    textAlign: 'left'
                  }}>
                    Role <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <CustomRoleSelect
                    value={newPersonForm.role || ''}
                    options={availableRoles}
                    placeholder="Select Role"
                    onChange={(val) => handleFormChange('role', val)}
                    onAddRole={(name) => handleAddNewRole('modal', name)}
                  />
                </div>
                {/* Show Welder ID field if role is Welder */}
                {newPersonForm.role && isWelderRoleName(newPersonForm.role) && (
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#374151',
                      textAlign: 'left'
                    }}>
                      Welder ID <span style={{ color: '#EF4444' }}>*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. W001"
                      value={newPersonForm.welderId}
                      onChange={(e) => handleFormChange('welderId', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '6px',
                        border: '1px solid #D1D5DB',
                        fontSize: '14px',
                        outline: 'none',
                        color: '#1F2937',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                      onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                    />
                  </div>
                )}

                {newPersonForm.role && isWelderRoleName(newPersonForm.role) && (
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#374151',
                      textAlign: 'left'
                    }}>
                      Station number
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. 1"
                      value={newPersonForm.allocatedStation || ''}
                      onChange={(e) => handleFormChange('allocatedStation', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '6px',
                        border: '1px solid #D1D5DB',
                        fontSize: '14px',
                        outline: 'none',
                        color: '#1F2937',
                        boxSizing: 'border-box',
                        backgroundColor: '#FFFFFF'
                      }}
                      onFocus={(e) => { e.target.style.borderColor = '#4F46E5'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#D1D5DB'; }}
                    />
                  </div>
                )}
              </div>

              {/* Footer */}
              <div style={{
                padding: '16px 24px',
                backgroundColor: '#F9FAFB',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                borderTop: '1px solid #E5E7EB'
              }}>
                <Button
                  label="Cancel"
                  variant="tertiary"
                  onClick={handleCloseModal}
                  size="medium"
                />
                <Button
                  label="Add Person"
                  variant="primary"
                  onClick={handleAddPerson}
                  disabled={!newPersonForm.fullName || !newPersonForm.role}
                  size="medium"
                />
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default PersonnelLog;

